import { makeAutoObservable, observable } from 'mobx';
import React from 'react';

import { Cookies } from 'core/cookies';
import { Permission } from 'framework/permission';
import { MonitorType, MonitorUiType } from 'areas/assets/monitors/monitorType';
import { LoginResponse } from 'core/api';
import { getUrlBuilderForCurrentHash } from 'tools/urlBuilder';
import { fromLegacyFormat } from 'tools/dateTime/fromLegacyFormat';
import { parseDateFormat } from 'tools/dateTime/parseDateFormat';
import { ModuleInitializedCallbackProps } from './modules';
import { Theme } from 'framework/theme/types';

// TODO: add other fields
export interface NotificationCounters {
	unreadMessages?: number;
	unreadSystemErrors?: number;
	eventSummaryCount?: number;
	lastChanges?: number;
}

class ApplicationStateInner {
	//this is not backend session. Backend session is stored in apiToken field
	//this is purely UI session to separate standalone instances from a default one
	sessionId: string;

	userName: string;
	userId: string;

	apiToken: string;
	accountId: string;
	accountName: string;
	userAccountId: string;
	userAccountName: string;
	accountLogoId: string;
	userPictureId: string;

	timezone: string;

	dateFormatLegacy: string;
	timeFormat: string;
	dateTimeFormatLegacy: string;

	locale: string;

	apiUrl: string = '/rest/';

	includeSubaccounts: boolean;
	includeSubaccountsMenuDisabled: boolean = false;

	standalone: boolean;

	enabledMonitors: {
		value: MonitorType;
		text: string;
		sharedAgents: boolean;
		uiType: MonitorUiType;
		monitorUrl?: string;
	}[] = [];

	userTags: string[] = [];
	ignoreTags: boolean;
	showUntagged: boolean;

	permissions: Permission[] = [];

	currentRouteId: string;
	currentHelpKey?: string;
	theme: Theme = Theme.Light;

	pageComponent: React.Component;
	moduleInitializationResult: ModuleInitializedCallbackProps;

	// the page takes the whole window: the site's menu and header are hidden; any page change ends it
	fullScreen = false;

	notificationCounters: NotificationCounters = {};

	regionalSchedulerDays: Record<string, number> = {
		MONDAY: 1,
		TUESDAY: 2,
		WEDNESDAY: 3,
		THURSDAY: 4,
		FRIDAY: 5,
		SATURDAY: 6,
		SUNDAY: 7,
	};

	constructor() {
		makeAutoObservable(this, {
			pageComponent: observable.ref,
		});
	}

	get isGuestUser() {
		return this.userName == 'public';
	}

	get dateFormat() {
		return fromLegacyFormat(this.dateFormatLegacy);
	}

	get dateTimeFormat() {
		return fromLegacyFormat(this.dateTimeFormatLegacy);
	}

	get momentDateTimeFormat() {
		return parseDateFormat(this.dateTimeFormat).replace('.SSS', '').replace(':ss', '');
	}

	get momentDateTimeFormatFull() {
		return parseDateFormat(this.dateTimeFormat);
	}

	get momentTimeFormat() {
		return parseDateFormat(this.timeFormat).replace('.SSS', '').replace(':ss', '');
	}

	get tagsEnabled() {
		return this.userTags?.length > 0 && !this.ignoreTags;
	}

	loadStateFromCookies() {
		this.apiToken = Cookies.get('sessionId') as string;
		this.accountId = Cookies.get('CeesoftCurrentAccountId') as string;
		this.accountName = Cookies.get('CeesoftCurrentAccountName') as string;
		this.userAccountId = Cookies.get('CeesoftAccountId') as string;
		this.userAccountName = Cookies.get('CeesoftAccountName') as string;
		this.dateFormatLegacy = Cookies.get('CeesoftUserDateFormat') as string;
		this.timeFormat = Cookies.get('CeesoftUserTimeFormat') as string;
		this.locale = Cookies.get('CeesoftUserLocale') as string;
		this.dateTimeFormatLegacy = Cookies.get('CeesoftUserDateTimeFormat') as string;
		this.timezone = Cookies.get('CeesoftTimezone') as string;
		this.userName = Cookies.get('CeesoftUsername') as string;
		this.userId = Cookies.get('CeesoftUserId') as string;
		this.userPictureId = Cookies.get('CeesoftUserPictureId') as string;
		this.accountLogoId = Cookies.get('CeesoftAccountLogoId') as string;
	}

	loadStateFromLoginResponse(loginData: LoginResponse) {
		this.apiToken = loginData.sessionId;
		this.accountId = loginData.loginAccountId;
		this.accountName = loginData.loginAccountName;
		this.dateFormatLegacy = loginData.dateFormat;
		this.dateTimeFormatLegacy = loginData.dateTimeFormat;
		this.timeFormat = loginData.timeFormat;
		this.timezone = loginData.timeZone;
		this.locale = loginData.locale;
		this.userName = loginData.username;
		this.userId = loginData.userId;
		this.userPictureId = loginData.pictureId;
		this.accountLogoId = loginData.logoId;
	}

	hasPermissions = (...permissions: Permission[]) => {
		return permissions.every((p) => this.permissions.includes(p));
	};

	hasAnyPermission = (...permissions: Permission[]): boolean => {
		return permissions.some((p) => this.hasPermissions(p));
	};
}

export const ApplicationState = new ApplicationStateInner();

export function checkForIsolatedSession() {
	const urlBuilder = getUrlBuilderForCurrentHash();
	const sessionId = urlBuilder.params.sessionId;
	if (sessionId != null) {
		Cookies.useSessionStorage(sessionId);
		ApplicationState.sessionId = sessionId;
	}

	ApplicationState.standalone = urlBuilder.params.standalone == '1' || Cookies.get('CeesoftUsername') == 'public';
}
